import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { setToken } from '@/utils/tokenManager';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      try {
        const match = location.hash.match(/session_id=([^&]+)/);
        if (!match) throw new Error('No session id in URL');

        const resp = await fetch(`${BACKEND_URL}/api/auth/google`, {
          method: 'POST',
          headers: { 'X-Session-ID': match[1], 'Content-Type': 'application/json' },
        });

        if (resp.status === 404) {
          toast.error(
            'Please create an account first with your name, email and password.',
            { duration: 6000 }
          );
          navigate('/login?tab=register', { replace: true });
          return;
        }
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || 'Google sign-in failed');
        }

        const data = await resp.json();
        if (data.token) setToken(data.token);
        toast.success('Welcome back — Quick View', { duration: 2500 });
        // Attach session_mode so ProtectedRoute + hooks see it before /auth/me runs.
        const enrichedUser = { ...(data.user || {}), session_mode: data.session_mode || 'quick_view' };
        navigate('/dashboard', { replace: true, state: { user: enrichedUser } });
      } catch (error) {
        logger.error('Google quick-view error:', error);
        toast.error(error.message || 'Sign-in failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    run();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <div className="text-center space-y-4" data-testid="auth-callback-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
        <p className="text-slate-600">Signing you in for a Quick View…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
